import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { products } from '../../mocks/products';
import { useCart } from '../../hooks/useCart';

export default function ProductsPage() {
  const { getCartCount } = useCart();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    document.title = 'All Radiators & Heating Products | PEL Partner Company Belgium';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Browse PEL Partner Company\'s full range of premium radiators, towel warmers and heating valves. Competitive pricing, fast delivery across Belgium, UK & Europe.');

    const schemaId = 'schema-products';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'All Radiators & Heating Products',
          description: "Browse PEL Partner Company's full range of premium radiators, towel warmers and heating valves.",
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
              { '@type': 'ListItem', position: 2, name: 'Products', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products` },
            ],
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Premium Radiators & Heating Products — PEL Partner Company',
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/products`,
          numberOfItems: products.length,
          itemListElement: products.map((p, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/product/${p.id}`,
            name: p.name,
          })),
        },
      ]);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const categories = ['All', 'Radiators', 'Towel Warmers', 'Valves'];

  const filteredProducts = products.filter((product) =>
    selectedCategory === 'All' ? true : product.category === selectedCategory
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar cartItemCount={getCartCount()} />

      {/* Page Header */}
      <section className="bg-[#F5F1E8] pt-32 pb-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h1 className="font-serif text-6xl font-bold text-black">Our Products</h1>
              <p className="text-gray-500 text-sm mt-2">{sortedProducts.length} items available</p>
            </div>
            <p className="text-gray-700 max-w-md text-sm leading-relaxed">
              Browse our full range of radiators, towel warmers, and valves. All prices include cost breakdown and stock availability.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Sort Bar */}
      <section className="bg-white shadow-sm sticky top-[72px] z-40">
        <div className="max-w-[1400px] mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-black'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 cursor-pointer hover:border-black transition-colors"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {sortedProducts.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border border-gray-100"
              >
                {/* Image */}
                <div className="relative overflow-hidden aspect-square">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  {product.status && (
                    <span className="absolute top-3 left-3 bg-black text-white px-3 py-1 rounded-full text-xs font-medium">
                      {product.status}
                    </span>
                  )}
                  {/* Stock badge */}
                  <span className="absolute top-3 right-3 bg-white/90 text-gray-700 px-2 py-1 rounded-full text-xs font-semibold border border-gray-200">
                    Stock: {product.stock}
                  </span>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <span className="bg-white text-black px-6 py-3 rounded-full text-sm font-medium whitespace-nowrap">
                      View Details
                    </span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  {/* Item No + Category */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{product.itemNo}</span>
                    <span className="text-xs text-gray-400">{product.category}</span>
                  </div>

                  <h3 className="text-sm font-semibold mb-1 text-black line-clamp-2 leading-snug">
                    {product.name}
                  </h3>

                  {/* Size */}
                  <div className="flex items-center gap-1 mb-2">
                    <i className="ri-ruler-line text-gray-400 text-xs"></i>
                    <span className="text-xs text-gray-500">{product.sizes[0]}</span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-${i < Math.floor(product.rating) ? 'fill' : 'line'} text-yellow-500 text-xs`}
                      ></i>
                    ))}
                    <span className="text-xs text-gray-400 ml-1">({product.reviews})</span>
                  </div>

                  {/* Price breakdown */}
                  <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Price / piece</span>
                      <span className="font-medium text-gray-700">${product.pricePerPiece.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Cost / piece</span>
                      <span className="font-medium text-gray-700">${product.costPerPiece.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-1 mt-1 flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-600">Total (×{product.stock})</span>
                      <span className="text-base font-bold text-black">${product.price.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {sortedProducts.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">No products found in this category.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
